import React, { useState } from 'react';
import Anthropic from '@anthropic-ai/sdk';
import { Dialog, DialogContent, DialogTitle, IconButton, Box, Typography, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { WEIGHTING } from '../Data/weighting';

export const useTechnologyAnalysis = () => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateTechnologyAnalysis = async (vendors) => {
    setLoading(true);
    setError(null);
    
    try {
      const client = new Anthropic({
        apiKey: "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",
        dangerouslyAllowBrowser: true
      });

      const response = await client.messages.create({
        model: 'claude-3-5-sonnet-20241022',
        max_tokens: 4096,
        messages: [{
          role: 'user',
          content: generateTechnologyPrompt(vendors)
        }],
        temperature: 0.7
      });
      setAnalysis(response.content[0].text);
    } catch (error) {
      console.error('Error generating technology analysis:', error);
      setError(error.message || 'An error occurred while generating analysis');
      setAnalysis('Error generating analysis. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return {
    analysis,
    loading,
    error,
    generateTechnologyAnalysis
  };
};

const generateTechnologyPrompt = (vendors) => {
  return `Please analyze the following technology vendors and provide a detailed comparison based on the weighted criteria:

Vendors: ${vendors.join(', ')}

For each vendor, please analyze:
1. Individual criteria scores and justification
2. Weighted impact of each criterion
3. Overall technology score
4. Strengths and weaknesses
5. Recommended use cases
6. Key differentiators

Consider the following weighted criteria:
${Object.entries(WEIGHTING.Criteria)
  .map(([criterion, weight]) => `- ${criterion}: ${weight}%`)
  .join('\n')}

Please provide:
1. Detailed analysis of each vendor
2. Comparative analysis across vendors
3. Recommendations based on specific use cases
4. Risk assessment
5. Implementation considerations

Focus on providing actionable insights and clear recommendations based on the weighted criteria.`;
};

// Insights Generation Hook
export const useInsightsGeneration = () => {
  const [analysis, setAnalysis] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateInsights = async (selectedData) => {
    setLoading(true);
    setError(null);
    
    try {
      const client = new Anthropic({
        apiKey: "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",
        dangerouslyAllowBrowser: true
      });

      const response = await client.messages.create({
        model: 'claude-3-5-sonnet-20241022',
        max_tokens: 4096,
        messages: [{
          role: 'user',
          content: generateInsightsPrompt(selectedData)
        }],
        temperature: 0.7
      });
      setAnalysis(response.content[0].text);
    } catch (error) {
      console.error('Error generating insights:', error);
      setError(error.message || 'An error occurred while generating insights');
      setAnalysis('Error generating insights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return {
    analysis,
    loading,
    error,
    generateInsights
  };
};

// Enhanced Insights Dialog Component
export const InsightsDialog = ({ open, handleClose, analysis, loading }) => {
  const highlightNumbers = (text) => {
    if (!text) return null;
    
    return text.split(/(\d+(?:,\d{3})*(?:\.\d+)?(?:\s*(?:sites|locations|USD|\$|euros|€))?)/g).map((part, index) => {
      if (part.match(/\d+(?:,\d{3})*(?:\.\d+)?(?:\s*(?:sites|locations|USD|\$|euros|€))?/)) {
        return (
          <Typography
            key={index}
            component="span"
            sx={{
              color: 'primary.main',
              fontWeight: 'bold'
            }}
          >
            {part}
          </Typography>
        );
      }
      return part;
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={loading ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '70vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <LightbulbOutlinedIcon sx={{ fontSize: 24 }} />
            <Typography variant="h6">Network Infrastructure Analysis</Typography>
          </Box>
          <IconButton 
            onClick={handleClose}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box 
            display="flex" 
            flexDirection="column"
            justifyContent="center" 
            alignItems="center" 
            minHeight="200px"
            gap={2}
          >
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary">
              Generating infrastructure insights...
            </Typography>
          </Box>
        ) : (
          <Typography
            component="div"
            sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              lineHeight: 1.6
            }}
          >
            {highlightNumbers(analysis || 'No insights available. Please try again.')}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Prompt Generator Function
const generateInsightsPrompt = (selectedData) => {
  const {
    filteredLocations,
    selectedVendors,
    selectedSiteTypes,
    costs,
    partnerCosts,
    infrastructureSetupCosts,
    transitionCosts,
    insourcedData,
    outsourcedData
  } = selectedData;

  // Calculate site statistics
  const totalSites = filteredLocations.filter(loc => selectedSiteTypes.includes(loc.size)).length;
  const sitesByType = selectedSiteTypes.reduce((acc, type) => {
    acc[type] = filteredLocations.filter(loc => loc.size === type).length;
    return acc;
  }, {});

  // Calculate geographical distribution
  const continents = [...new Set(filteredLocations.map(l => l.continent))];
  const countries = [...new Set(filteredLocations.map(l => l.country))];

  // Calculate vendor cost analysis
  const vendorCosts = Object.entries(costs).map(([vendor, cost]) => ({
    vendor,
    cost,
    costPerSite: cost / totalSites
  }));

  return `Please analyze the following network infrastructure data and provide a comprehensive report:

Site Distribution Analysis:
- Total Selected Sites: ${totalSites} sites across ${continents.length} continents and ${countries.length} countries
- Geographical Spread: ${continents.join(', ')}
- Site Type Breakdown: ${Object.entries(sitesByType).map(([type, count]) => `${type}: ${count} sites`).join(', ')}

Technology Stack:
- Selected Vendors: ${selectedVendors.join(', ')}
- Vendor Cost Analysis:
${vendorCosts.map(v => `  * ${v.vendor}: Total Cost ${v.cost}, Cost per Site ${v.costPerSite}`).join('\n')}

Financial Data:
- Infrastructure Setup Costs: ${JSON.stringify(infrastructureSetupCosts)}
- Transition Costs: ${JSON.stringify(transitionCosts)}
- Partner Costs: ${JSON.stringify(partnerCosts)}
- Insourced Model Data: ${JSON.stringify(insourcedData)}
- Outsourced Model Data: ${JSON.stringify(outsourcedData)}

Please provide:
1. Executive Summary
2. Technology Recommendation
   - Compare vendors based on cost per site type
   - Analyze pros and cons of each vendor
   - Recommend optimal vendor selection based on site distribution
3. Geographical Implementation Strategy
   - Analyze regional requirements
   - Suggest phased rollout approach
4. Financial Analysis
   - TCO comparison
   - Cost optimization opportunities
   - ROI analysis
5. Risk Assessment
   - Technical risks
   - Operational risks
   - Geographical risks
6. Implementation Timeline
7. Key Success Factors
8. Next Steps and Recommendations
9. Give a synopsis for the best experience from an operational perspective

Focus on providing actionable insights and clear recommendations based on the data provided. Consider factors such as:
- Regional compliance requirements
- Technical compatibility across different site types
- Cost optimization opportunities
- Operational efficiency
- Long-term scalability
- Risk mitigation strategies`;
};

export default InsightsDialog;